import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import Chip from "@mui/material/Chip";
import { getCapturedImageDisplayUrl } from "../../services/s3/captureUrlService";

/**
 * Card component to display the captured image used for face matching
 * Shows alongside matched person cards to provide context
 *
 * CHANGED 2026-05-30: Now uses direct public URLs instead of signed URLs
 * - No loading state needed (instant URL construction)
 * - No async fetch required
 */
export default function CapturedImageCard({ s3Key, matchAccuracy }) {
  // Generate public URL directly (synchronous, no loading needed)
  const imageUrl = React.useMemo(() => {
    return getCapturedImageDisplayUrl(s3Key);
  }, [s3Key]);

  if (!imageUrl) {
    return null; // Don't show card if no S3 key provided
  }

  return (
    <Box>
      <Card
        sx={{
          width: 150,
          position: 'relative',
          border: '2px solid',
          borderColor: 'primary.main',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          }
        }}
      >
        {/* Badge showing this is the captured image */}
        <Chip
          icon={<CameraAltIcon />}
          label="Your Photo"
          color="primary"
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            fontWeight: 600,
            fontSize: '0.7rem'
          }}
        />

        <CardMedia
          component="img"
          height={150}
          image={imageUrl}
          alt="Captured image"
          sx={{
            objectFit: 'cover',
            backgroundColor: 'grey.100'
          }}
        />

        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack spacing={1} alignItems="center">
            <Typography
              variant="body2"
              align="center"
              sx={{
                fontWeight: 500,
                lineHeight: 1.2,
                fontSize: '0.75rem',
                minHeight: 28
              }}
            >
              Captured Image
            </Typography>

            {matchAccuracy && (
              <Typography
                variant="caption"
                align="center"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem'
                }}
              >
                {(matchAccuracy * 100).toFixed(1)}% match
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
